import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Link2, QrCode, RefreshCw } from 'lucide-react';
import QRCode from 'qrcode';
import { suggestRosterMatch, type ApproveJoinInput, type JoinLink, type JoinRequest, type MyJoinRequest, type RosterPlayer, type TeamDetail } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { timeAgo } from '../../lib/format';
import { Alert, Button, Dialog, Section } from '../ui';

// ---------- The link ----------

/** The team's join link: copy it into a group chat, or show the QR code. */
export function JoinLinkSection({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const key = ['join-link', team.id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<JoinLink>(`/teams/${team.id}/join-link`) });
  const set = (l: JoinLink) => qc.setQueryData(key, l);
  const make = useMutation({ mutationFn: () => api.post<JoinLink>(`/teams/${team.id}/join-link`), onSuccess: set });
  const toggle = useMutation({ mutationFn: (enabled: boolean) => api.patch<JoinLink>(`/teams/${team.id}/join-link`, { enabled }), onSuccess: set });
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const l = q.data;
  const error = make.error ?? toggle.error;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(l!.url!);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the box is selectable as a fallback */
    }
  };

  return (
    <Section title="Join link" description="Share it in the team’s group chat. Players and parents use it to ask to join, and you approve each person.">
      {!l ? null : !l.code ? (
        <Button loading={make.isPending} onClick={() => make.mutate()}>
          <Link2 className="size-4" aria-hidden /> Make a join link
        </Button>
      ) : (
        <div className="max-w-[640px] space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              readOnly
              value={l.url!}
              onFocus={(e) => e.target.select()}
              aria-label="Join link"
              className={`field-input min-w-[220px] flex-1 font-mono text-[14px] ${l.enabled ? '' : 'text-faint line-through'}`}
            />
            <Button onClick={copy} disabled={!l.enabled}>
              {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />} {copied ? 'Copied' : 'Copy'}
            </Button>
            <Button variant="secondary" onClick={() => setQr(true)} disabled={!l.enabled}>
              <QrCode className="size-4" aria-hidden /> QR code
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[14px]">
            <label className="flex cursor-pointer items-center gap-2">
              <input type="checkbox" className="size-4 accent-[var(--action)]" checked={l.enabled} disabled={toggle.isPending} onChange={(e) => toggle.mutate(e.target.checked)} />
              {l.enabled ? 'Link is on' : 'Link is off: nobody can use it'}
            </label>
            <button className="inline-flex items-center gap-1.5 font-medium text-action hover:underline" onClick={() => setConfirmReset(true)}>
              <RefreshCw className="size-3.5" aria-hidden /> Make a new link
            </button>
          </div>
          <p className="text-[13px] text-muted">Anyone with the link can ask, but nobody gets in until you or another coach approves them.</p>
        </div>
      )}
      {error && <div className="mt-3"><Alert>{errorMessage(error)}</Alert></div>}
      {l?.url && <QrDialog open={qr} onClose={() => setQr(false)} url={l.url} teamName={team.name} />}
      <Dialog open={confirmReset} onClose={() => setConfirmReset(false)} title="Make a new join link?">
        <p className="text-muted">The current link (and its QR code) stops working straight away. Use this if the link has been shared somewhere it shouldn’t be.</p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmReset(false)}>
            Keep this link
          </Button>
          <Button loading={make.isPending} onClick={() => make.mutate(undefined, { onSuccess: () => setConfirmReset(false) })}>
            Make a new link
          </Button>
        </div>
      </Dialog>
    </Section>
  );
}

function QrDialog({ open, onClose, url, teamName }: { open: boolean; onClose: () => void; url: string; teamName: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    void QRCode.toDataURL(url, { width: 640, margin: 2, errorCorrectionLevel: 'M', color: { dark: '#0B0B14', light: '#FFFFFF' } }).then(setSrc);
  }, [open, url]);
  return (
    <Dialog open={open} onClose={onClose} title={`Join ${teamName}`}>
      <div className="flex flex-col items-center gap-3">
        {src && <img src={src} alt={`QR code for ${url}`} className="size-64 rounded-lg bg-white p-2" />}
        <p className="text-center font-mono text-[13px] break-all text-muted">{url}</p>
        <p className="text-center text-[14px] text-muted">Point a phone camera at it. Good for a printed sheet or a parents’ night.</p>
        {src && (
          <a href={src} download={`join-${teamName.replace(/[^\w]+/g, '-').toLowerCase()}.png`} className="btn-secondary">
            Download the QR code
          </a>
        )}
      </div>
    </Dialog>
  );
}

// ---------- Requests ----------

type Choice = 'none' | 'new' | string;

/** People waiting to join from the link (once they've confirmed their email). */
export function JoinRequestsSection({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const key = ['join-requests', team.id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<JoinRequest[]>(`/teams/${team.id}/join-requests`) });
  const roster = useQuery({ queryKey: ['roster', team.id], queryFn: () => api.get<RosterPlayer[]>(`/teams/${team.id}/roster`) });
  const after = (list: JoinRequest[]) => {
    qc.setQueryData(key, list);
    void qc.invalidateQueries({ queryKey: ['members', team.id] });
    void qc.invalidateQueries({ queryKey: ['roster', team.id] });
    void qc.invalidateQueries({ queryKey: ['team', team.id] });
  };
  const approve = useMutation({
    mutationFn: ({ id, link }: { id: string; link: ApproveJoinInput['link'] }) => api.post<JoinRequest[]>(`/teams/${team.id}/join-requests/${id}/approve`, { link }),
    onSuccess: after,
  });
  const decline = useMutation({ mutationFn: (id: string) => api.post<JoinRequest[]>(`/teams/${team.id}/join-requests/${id}/decline`), onSuccess: after });
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const list = q.data ?? [];
  if (!list.length) return null;
  const players = roster.data ?? [];

  const choiceFor = (r: JoinRequest): Choice => choices[r.id] ?? suggestRosterMatch(r.playerName, players)?.id ?? 'new';
  const linkFor = (c: Choice): ApproveJoinInput['link'] => (c === 'none' ? { kind: 'none' } : c === 'new' ? { kind: 'new' } : { kind: 'player', playerId: c });
  const error = approve.error ?? decline.error;

  return (
    <Section title={`Waiting for approval (${list.length})`} description="They asked to join from the join link. They can’t see anything until you approve them.">
      {error && <div className="mb-3"><Alert>{errorMessage(error)}</Alert></div>}
      <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
        {list.map((r) => {
          const c = choiceFor(r);
          const suggested = suggestRosterMatch(r.playerName, players);
          const busy = (approve.isPending && approve.variables?.id === r.id) || (decline.isPending && decline.variables === r.id);
          return (
            <li key={r.id} className="space-y-2 px-4 py-3">
              <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                <p className="min-w-0">
                  <span className="font-medium">{r.name}</span>{' '}
                  <span className="text-[14px] text-muted">
                    {r.role === 'parent' ? `parent of ${r.playerName}` : r.playerName === r.name ? 'player' : `player (“${r.playerName}”)`}
                  </span>
                </p>
                <span className="text-[13px] text-muted">{timeAgo(r.requestedAt)}</span>
              </div>
              <p className="truncate text-[13px] text-muted">{r.email}</p>
              <div className="flex flex-wrap items-center gap-2">
                <label className="sr-only" htmlFor={`link-${r.id}`}>
                  Link {r.name} to
                </label>
                <select id={`link-${r.id}`} className="field-input w-auto min-w-0 flex-1 py-1.5 text-[14px] sm:flex-none" value={c} onChange={(e) => setChoices({ ...choices, [r.id]: e.target.value })}>
                  {players.length > 0 && (
                    <optgroup label="Link to a roster player">
                      {players.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.number ? `${p.number} ` : ''}
                          {p.name}
                          {suggested?.id === p.id ? ' (looks like a match)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <option value="new">Add “{r.playerName}” to the roster</option>
                  <option value="none">Don’t link to a player</option>
                </select>
                <Button loading={approve.isPending && approve.variables?.id === r.id} disabled={busy} onClick={() => approve.mutate({ id: r.id, link: linkFor(c) })}>
                  Approve
                </Button>
                <Button variant="secondary" loading={decline.isPending && decline.variables === r.id} disabled={busy} onClick={() => decline.mutate(r.id)}>
                  Decline
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

// ---------- Your own requests ----------

/** "Waiting for the coach to approve you" on the Teams and Schedule pages. */
export function MyJoinRequests() {
  const q = useQuery({ queryKey: ['join-requests', 'mine'], queryFn: () => api.get<MyJoinRequest[]>('/join/requests/mine'), staleTime: 60_000 });
  const list = q.data ?? [];
  if (!list.length) return null;
  return (
    <div className="mb-5 space-y-2">
      {list.map((r) => (
        <Alert key={r.teamName + r.requestedAt} tone={r.status === 'declined' ? 'warn' : 'info'}>
          {r.status === 'declined'
            ? `The coach of ${r.teamName} didn’t approve your request to join.`
            : r.emailConfirmed
              ? `You’ve asked to join ${r.teamName}. You’ll see the team here once a coach approves you.`
              : `You’ve asked to join ${r.teamName}. Confirm your email address (check your inbox) so the coach can see your request.`}
        </Alert>
      ))}
    </div>
  );
}
